import { formatBytes } from './format/bytes'

export const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.heic', '.webp'] as const

/**
 * Largest single file we accept — a MEMORY bound, not a policy one.
 *
 * Two places hold a whole file at once: the app buffers the multipart body before forwarding it, and
 * docproc keeps the PDF resident while `rasterizeOnePage` renders pages at `OCR_PAGE_CONCURRENCY` in
 * parallel (parseDocument.ts). Peak RAM is therefore file + N rasters, per concurrent upload, and a
 * bulk drop multiplies it. 50 MB keeps that bounded on a small Cloud Run instance.
 *
 * There is deliberately NO page limit: routing already defers anything over ~5 printed pages (and all
 * handwriting) to a human confirm, so long documents can't run up a bill unattended.
 *
 * Shared so the client pre-check, the API and docproc cannot disagree about the number.
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024

export const MAX_UPLOAD_LABEL = formatBytes(MAX_UPLOAD_BYTES)

export function isTooLarge(sizeBytes: number): boolean {
  return sizeBytes > MAX_UPLOAD_BYTES
}

export const ALLOWED_ACCEPT = ALLOWED_EXTENSIONS.join(',')

export function isAllowedFile(filename: string): boolean {
  const name = filename.toLowerCase()
  return ALLOWED_EXTENSIONS.some(ext => name.endsWith(ext))
}

/**
 * Split a drop/select into what the app will upload and what it will not, in one pass.
 *
 * Every upload entry point used to run its own `files.filter(f => isAllowedFile(f.name))` and throw
 * the rest away with nothing kept to report — a `.zip` (or any other disallowed type) vanished with
 * no dialog, no toast, and no console error (AlexAmin/marcus#804). This is the one place that split
 * happens now, so a caller always has the rejected files in hand to name in an error rather than
 * dropping them silently.
 */
export function partitionAllowedFiles<T extends { name: string }>(files: T[]): { valid: T[]; disallowed: T[] } {
  const valid: T[] = []
  const disallowed: T[] = []
  for (const f of files) {
    (isAllowedFile(f.name) ? valid : disallowed).push(f)
  }
  return { valid, disallowed }
}

export function isImageFile(filename: string): boolean {
  const name = filename.toLowerCase()
  return ['.jpg', '.jpeg', '.png', '.heic', '.webp'].some(ext => name.endsWith(ext))
}

/** MIME → extension, for the image types a clipboard can hand us. */
const IMAGE_MIME_EXTENSIONS: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/heic': '.heic',
  'image/webp': '.webp',
}

/**
 * A filename an upload will accept, for an image that arrived without a usable one.
 *
 * Clipboard images are named by whatever produced them: usually `image.png`, sometimes a bare stem
 * with no extension, occasionally nothing. `isAllowedFile` gates uploads on the extension and the
 * upload path drops what it does not recognise SILENTLY — so a paste with no extension would vanish
 * without an error anywhere (MAR-491). Deriving the extension from the MIME type instead makes the
 * accept decision follow what the file IS rather than what its source felt like calling it.
 *
 * A name that already carries an allowed extension is returned untouched — this renames only what
 * would otherwise be discarded. `stem` is caller-supplied so this stays pure and localizable; it is
 * cosmetic, since two pastes of one picture de-duplicate on bytes rather than on their names.
 */
export function uploadableImageName(originalName: string, mimeType: string, stem: string): string {
  if (isAllowedFile(originalName)) return originalName
  return `${stem}${IMAGE_MIME_EXTENSIONS[mimeType] ?? '.png'}`
}
